import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { PanelPageHeader } from './PanelPageHeader';

interface TechnicalSheetData {
  id: string;
  documentId: string;
  synthesis: string;
  backCoverText: string;
  synopsis: string;
  summary: string | null;
  extension: number;
  genre: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TechnicalSheetProps {
  isVisible: boolean;
  token: string | null;
  documentId: string | undefined;
  t: (key: string) => string;
  showErrorToast: (message: string) => void;
  isSidebar?: boolean;
  hideHeading?: boolean;
  onClose?: () => void;
}

export const TechnicalSheet: React.FC<TechnicalSheetProps> = ({
  isVisible,
  token,
  documentId,
  t,
  showErrorToast,
  isSidebar = false,
  hideHeading = false,
  onClose,
}) => {
  const [technicalSheet, setTechnicalSheet] = useState<TechnicalSheetData | null>(null);
  const [isLoadingTechnicalSheet, setIsLoadingTechnicalSheet] = useState<boolean>(false);
  const [isRegeneratingTechnicalSheet, setIsRegeneratingTechnicalSheet] = useState<boolean>(false);

  useEffect(() => {
    const loadTechnicalSheet = async (): Promise<void> => {
      if (!isVisible || !token || !documentId || documentId === 'new') {
        return;
      }

      setIsLoadingTechnicalSheet(true);
      try {
        const response = await fetch(`${API_BASE}/api/documents/${documentId}/technical-sheet`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setTechnicalSheet(data);
        } else if (response.status === 404) {
          setTechnicalSheet(null);
        } else {
          console.error('Failed to load technical sheet');
        }
      } catch (err) {
        console.error('Error fetching technical sheet:', err);
      } finally {
        setIsLoadingTechnicalSheet(false);
      }
    };

    loadTechnicalSheet();
  }, [isVisible, token, documentId]);

  const regenerateTechnicalSheet = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      console.warn('Cannot regenerate technical sheet:', { token: !!token, documentId, isNew: documentId === 'new' });
      return;
    }

    setIsRegeneratingTechnicalSheet(true);
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/technical-sheet/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTechnicalSheet(data);
      } else {
        let errorMessage = t('failed_to_regenerate_technical_sheet');
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          errorMessage = `Failed to regenerate technical sheet: ${response.status} ${response.statusText}`;
        }
        console.error('Failed to regenerate technical sheet:', response.status, errorMessage);
        showErrorToast(errorMessage);
      }
    } catch (err) {
      console.error('Error regenerating technical sheet:', err);
      showErrorToast(t('failed_to_regenerate_technical_sheet'));
    } finally {
      setIsRegeneratingTechnicalSheet(false);
    }
  };

  const regenerateButton = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        regenerateTechnicalSheet();
      }}
      disabled={isRegeneratingTechnicalSheet}
      style={{
        backgroundColor: 'var(--scribane-black)',
        color: '#ffffff',
        border: 'none',
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        cursor: isRegeneratingTechnicalSheet ? 'not-allowed' : 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
        opacity: isRegeneratingTechnicalSheet ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {isRegeneratingTechnicalSheet ? t('regenerating') : t('regenerate_technical_sheet')}
    </button>
  );

  const textBlockStyle: React.CSSProperties = {
    padding: '1rem',
    borderRadius: '0.5rem',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
  };

  return (
    <div
      style={{
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        contain: 'inline-size',
        ...(isSidebar ? { padding: '1rem' } : {}),
      }}
    >
      {hideHeading ? null : (
        <PanelPageHeader
          onClose={!isSidebar ? onClose : undefined}
          closeAriaLabel={t('close')}
          actions={regenerateButton}
        />
      )}
      {hideHeading && !isSidebar ? (
        <div style={{ marginBottom: '2rem' }}>{regenerateButton}</div>
      ) : null}
      {isLoadingTechnicalSheet ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>{t('loading')}</div>
      ) : technicalSheet ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', minWidth: 0, maxWidth: '100%' }}>
          <div style={{ minWidth: 0, maxWidth: '100%' }}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600 }}>{t('synthesis')}</h3>
            <div style={textBlockStyle} className="bg-muted text-muted-foreground">
              {technicalSheet.synthesis}
            </div>
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600 }}>{t('back_cover_text')}</h3>
            <div style={textBlockStyle} className="bg-muted text-muted-foreground">
              {technicalSheet.backCoverText}
            </div>
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600 }}>{t('synopsis')}</h3>
            <div style={textBlockStyle} className="bg-muted text-muted-foreground">
              {technicalSheet.synopsis}
            </div>
          </div>
          {technicalSheet.summary && (
            <div>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600 }}>{t('summary')}</h3>
              <div style={textBlockStyle} className="bg-muted text-muted-foreground">
                {technicalSheet.summary}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('extension')}</h3>
              <div style={{ color: 'var(--muted-foreground)' }}>
                {technicalSheet.extension.toLocaleString()} {t('words')}
              </div>
            </div>
            {technicalSheet.genre && (
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('genre')}</h3>
                <div style={{ color: 'var(--muted-foreground)' }}>{technicalSheet.genre}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', minWidth: 0, maxWidth: '100%' }}>
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>
            <p style={{ margin: '0 0 1.5rem 0' }}>{t('no_technical_sheet')}</p>
            <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              regenerateTechnicalSheet();
            }}
            disabled={isRegeneratingTechnicalSheet}
            style={{
              backgroundColor: 'var(--scribane-black)',
              color: '#ffffff',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              cursor: isRegeneratingTechnicalSheet ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 500,
              opacity: isRegeneratingTechnicalSheet ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {isRegeneratingTechnicalSheet ? t('regenerating') : t('regenerate_technical_sheet')}
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

